import { calculateResalePricing } from "../../../server/src/lib/resalePricing";
import type { PortalQuoteMarkup } from "../actions/itinerary.actions";

/** Editable markup values as the agent types them. */
export type MarkupDraft = {
  markupBasis?: "taxExclusive" | "taxInclusive";
  taxBase?: "resale" | "markup" | "taxInclusiveResale";
  optionalPolicies?: PortalQuoteMarkup["optionalPolicies"];
  markupType: "percentage" | "fixed";
  markupValue: number | null;
  gstEnabled: boolean;
  gstPercent: number;
};

/** Default GST rate offered when the agent turns GST on. */
export const DEFAULT_GST_PERCENT = 5;

export const emptyMarkupDraft = (): MarkupDraft => ({
  markupType: "percentage",
  markupValue: null,
  gstEnabled: false,
  gstPercent: DEFAULT_GST_PERCENT,
});

/** Converts a saved markup (or none) into editable values. */
export const markupToDraft = (markup: PortalQuoteMarkup | null | undefined): MarkupDraft =>
  markup
    ? {
        markupType: markup.markupType,
        markupBasis: markup.markupBasis, taxBase: markup.taxBase, optionalPolicies: markup.optionalPolicies,
        markupValue: markup.markupValue || null,
        gstEnabled: markup.gstPercent > 0,
        gstPercent: markup.gstPercent > 0 ? markup.gstPercent : DEFAULT_GST_PERCENT,
      }
    : emptyMarkupDraft();

/** Converts editable values into the API payload, or null when there is nothing to save. */
export const draftToMarkup = (draft: MarkupDraft): PortalQuoteMarkup | null => {
  const markupValue = Number(draft.markupValue) || 0;
  const gstPercent = draft.gstEnabled ? Number(draft.gstPercent) || 0 : 0;
  return markupValue > 0 || gstPercent > 0 || Object.keys(draft.optionalPolicies || {}).length > 0 ? { markupType: draft.markupType, markupValue, gstPercent, markupBasis: draft.markupBasis, taxBase: draft.taxBase, optionalPolicies: draft.optionalPolicies } : null;
};

/** The amounts behind a client price: the markup, the GST on the marked-up price, and the client total. */
export type MarkupBreakdown = { markupAmount: number; gstAmount: number; clientTotal: number; markupFactor: number };

/**
 * Previews the server's explicit markup and tax bases using the same pure resale calculator.
 */
export const computeMarkupBreakdown = (supplierTotal: number, markup: PortalQuoteMarkup | null, supplierTax = 0, currency = "INR"): MarkupBreakdown => {
  if (!markup || !markup.markupBasis || !markup.taxBase) return { markupAmount: 0, gstAmount: 0, clientTotal: supplierTotal, markupFactor: 1 };
  const result = calculateResalePricing(currency, [{ key: "party", quantity: 1, amount: supplierTotal, supplierTax }], markup);
  return { markupAmount: result.markup, gstAmount: result.agentTax, clientTotal: result.total, markupFactor: supplierTotal ? result.total / supplierTotal : 1 };
};

/** Client price for a supplier total; see `computeMarkupBreakdown`. */
export const computeClientTotal = (supplierTotal: number, markup: PortalQuoteMarkup | null, supplierTax = 0, currency = "INR"): number =>
  computeMarkupBreakdown(supplierTotal, markup, supplierTax, currency).clientTotal;
